import React, { useState, useCallback } from "react";
import { Helmet } from "react-helmet";
import { useParams, useNavigate } from "react-router-dom";
import { useStateContext } from "../context/ContextProvider";

/** Client-side cap aligned with server TestPayment:MaxAmount; override via REACT_APP_TEST_PAYMENT_MAX_AMOUNT */
const CLIENT_MAX_AMOUNT = (() => {
  const raw = process.env.REACT_APP_TEST_PAYMENT_MAX_AMOUNT;
  const v = raw === undefined || raw === "" ? NaN : Number.parseFloat(raw);
  return Number.isFinite(v) && v > 0 ? v : 5;
})();

const ORDER_ID_PATTERN = /^\d{6,32}$/;
const INSTALLMENT_MONTHS = [2, 3, 6, 9, 12, 18, 24];
const RRN_PATTERN = /^\d{12}$/;
const INT_REF_PATTERN = /^[A-Za-z0-9]{16,32}$/;

const TABS = {
  plain: "plain",
  installment: "installment",
  reversal: "reversal",
};

/**
 * Open checkout HTML in a new browsing context (user click). Required because
 * MPI pages typically set X-Frame-Options — we do not embed in an iframe.
 */
function openBlobHtmlInNewWindow(html) {
  const blob = new Blob([html], { type: "text/html;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const w = window.open(url, "_blank", "noopener,noreferrer");
  if (w) {
    try {
      w.opener = null;
    } catch {
      /* noop */
    }
    setTimeout(() => URL.revokeObjectURL(url), 120_000);
    return true;
  }
  return false;
}

/**
 * Internal test payment tools: dedicated TestPayment endpoints only; response is text/html.
 */
const TestPaymentGatewayPage = () => {
  const { lang: langParam } = useParams();
  const lang = langParam || "az";
  const navigate = useNavigate();
  const { token, setToken } = useStateContext();

  const [activeTab, setActiveTab] = useState(TABS.plain);

  const [amount, setAmount] = useState("1");
  const [name, setName] = useState("TEST");
  const [orderId, setOrderId] = useState("");
  const [installmentMonths, setInstallmentMonths] = useState(6);

  const [revAmount, setRevAmount] = useState("15");
  const [revOrderId, setRevOrderId] = useState("");
  const [rrn, setRrn] = useState("");
  const [intRef, setIntRef] = useState("");

  const [busy, setBusy] = useState(false);
  const [info, setInfo] = useState("");
  const [error, setError] = useState("");
  /** Pending server HTML; opened only via explicit click (pop-up + X-Frame-Options). */
  const [checkoutHtml, setCheckoutHtml] = useState(null);

  const openCheckoutHtmlInNewWindow = useCallback(() => {
    if (!checkoutHtml) return;
    const ok = openBlobHtmlInNewWindow(checkoutHtml);
    if (ok) {
      setError("");
      setInfo("Ödəniş formu yeni pəncərədə açıldı.");
    } else {
      setError(
        "Brauzer yeni pəncərəni blokladı. Ünvan sətirində pop-up icazəsi verin və ya düyməyə yenidən basın."
      );
    }
  }, [checkoutHtml]);

  const applyHttpResult = useCallback(
    (response, text) => {
      if (response.status === 401) {
        setCheckoutHtml(null);
        setToken(null);
        setError(
          "401: JWT yoxdur və ya etibarsızdır. Giriş səhifəsinə yönləndirilirsiniz."
        );
        navigate(`/${lang}/sign_in`, { replace: true });
        return;
      }

      if (response.status === 404) {
        setCheckoutHtml(null);
        setError(
          "404: test ödəniş serverdə söndürülüb (TestPayment:Enabled false), yanlış ünvan və ya funksiya mövcud deyil. Bu daxili səhifəni UI-dan gizlədin.\n\n" +
            text.slice(0, 2000)
        );
        return;
      }

      if (response.status === 400) {
        setCheckoutHtml(null);
        setError(
          (text && text.trim()) ||
            "400: məbləğ aralığı, test terminalı, orderId formatı və s. Yoxlayın."
        );
        return;
      }

      if (!response.ok) {
        setCheckoutHtml(null);
        setError(
          `HTTP ${response.status}. Cavab mətni (JSON deyil):\n\n${text.slice(0, 4000)}`
        );
        return;
      }

      setError("");
      setInfo(
        "Form hazırdır. MPI səhifəsini iframe-də göstərmirik (adətən X-Frame-Options). Aşağıdakı düymə ilə tam pəncərədə açın."
      );
      setCheckoutHtml(text);
    },
    [lang, navigate, setToken]
  );

  const runTestPaymentFetch = async (endpointPath, jsonBody) => {
    const base = process.env.REACT_APP_BASE_URL;
    if (!base) {
      setError("REACT_APP_BASE_URL təyin olunmayıb.");
      return;
    }

    setBusy(true);
    setError("");
    setInfo("");
    setCheckoutHtml(null);

    try {
      const response = await fetch(
        `${base.replace(/\/$/, "")}/api/TestPayment/${endpointPath}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(jsonBody),
        }
      );
      const text = await response.text();
      applyHttpResult(response, text);
    } catch (err) {
      setCheckoutHtml(null);
      setError(err?.message || "Şəbəkə xətası.");
    } finally {
      setBusy(false);
    }
  };

  const validateCommonAmountAndOrder = (num, oid) => {
    if (!Number.isFinite(num) || num <= 0 || num > CLIENT_MAX_AMOUNT) {
      return `amount 0-dan böyük və ən çox ${CLIENT_MAX_AMOUNT} olmalıdır (server TestPayment:MaxAmount ilə uyğunlaşdırın).`;
    }
    if (oid && !ORDER_ID_PATTERN.test(oid)) {
      return "orderId: yalnız rəqəm (0–9), uzunluq 6–32 simvol. Boş buraxsanız server özü yaradır.";
    }
    return null;
  };

  const handlePlainSubmit = async (e) => {
    e.preventDefault();
    if (!token) {
      setError(
        "JWT tapılmadı. Əvvəlcə daxil olun (Authorization: Bearer access_token)."
      );
      return;
    }

    const trimmedName = name.trim();
    const oid = orderId.trim();
    const num = Number(amount);
    const err = validateCommonAmountAndOrder(num, oid);
    if (err) {
      setError(err);
      return;
    }

    const body = { amount: num };
    if (trimmedName) body.name = trimmedName;
    if (oid) body.orderId = oid;

    await runTestPaymentFetch("build-form", body);
  };

  const handleInstallmentSubmit = async (e) => {
    e.preventDefault();
    if (!token) {
      setError(
        "JWT tapılmadı. Əvvəlcə daxil olun (Authorization: Bearer access_token)."
      );
      return;
    }

    const trimmedName = name.trim();
    const oid = orderId.trim();
    const num = Number(amount);
    const err = validateCommonAmountAndOrder(num, oid);
    if (err) {
      setError(err);
      return;
    }

    if (!INSTALLMENT_MONTHS.includes(Number(installmentMonths))) {
      setError(
        `installmentMonths yalnız bu dəyərlərdən biri ola bilər: ${INSTALLMENT_MONTHS.join(", ")}.`
      );
      return;
    }

    const body = {
      amount: num,
      installmentMonths: Number(installmentMonths),
    };
    if (trimmedName) body.name = trimmedName;
    if (oid) body.orderId = oid;

    await runTestPaymentFetch("build-installment-form", body);
  };

  const handleReversalSubmit = async (e) => {
    e.preventDefault();
    if (!token) {
      setError(
        "JWT tapılmadı. Əvvəlcə daxil olun (Authorization: Bearer access_token)."
      );
      return;
    }

    const oid = revOrderId.trim();
    if (!ORDER_ID_PATTERN.test(oid)) {
      setError(
        "orderId: mütləqdir — yalnız rəqəm, uzunluq 6–32 simvol (reversal üçün)."
      );
      return;
    }

    const num = Number(revAmount);
    if (!Number.isFinite(num) || num <= 0 || num > CLIENT_MAX_AMOUNT) {
      setError(
        `amount 0-dan böyük və ən çox ${CLIENT_MAX_AMOUNT} olmalıdır.`
      );
      return;
    }

    const rrnTrim = rrn.trim();
    if (!RRN_PATTERN.test(rrnTrim)) {
      setError("rrn: dəqiq 12 rəqəm olmalıdır.");
      return;
    }

    const intRefTrim = intRef.trim();
    if (!INT_REF_PATTERN.test(intRefTrim)) {
      setError(
        "intRef: 16–32 simvol, yalnız hərf və rəqəm (A–Z, a–z, 0–9)."
      );
      return;
    }

    await runTestPaymentFetch("build-reversal-form", {
      amount: num,
      orderId: oid,
      rrn: rrnTrim,
      intRef: intRefTrim,
    });
  };

  const onOrderIdDigits = (value, setter) =>
    setter(value.replace(/\D/g, "").slice(0, 32));

  const onRrnDigits = (value) =>
    setRrn(value.replace(/\D/g, "").slice(0, 12));

  const onIntRefAlnum = (value) =>
    setIntRef(value.replace(/[^A-Za-z0-9]/g, "").slice(0, 32));

  return (
    <div className="container py-5" style={{ maxWidth: 640 }}>
      <Helmet>
        <title>Test ödəniş qapısı</title>
      </Helmet>

      <h1 className="h4 mb-3">Test ödəniş (daxili)</h1>
      <p className="text-muted small mb-2">
        Üç ayrıca <code className="small">TestPayment</code> endpoint-i — adi
        faktura / invoice API-si deyil. Cavab həmişə{" "}
        <code className="small">text/html</code>;{" "}
        <code className="small">response.text()</code>, heç vaxt{" "}
        <code className="small">.json()</code> yox.
      </p>
      <p className="text-muted small mb-3">
        Uğurlu cavabdan sonra bank HTML-i <strong>avtomatik yeni pəncərədə
        açılmır</strong> (brauzer &quot;user activation&quot; qaydası). MPI
        tərəfi də adətən <code>X-Frame-Options</code> ilə iframe-i qadağan
        edir — ona görə <strong>yalnız</strong> aşağıdakı düymə ilə tam
        pəncərədə (blob URL) açılır; çərçivə istifadə olunmur.
      </p>
      <p className="text-muted small mb-4">
        Bu axınlar səbəti yeniləmir.{" "}
        <code className="small">/payment-result</code> qayıdışını test
        nəticəsi kimi qəbul edin. Taksit üçün MAC-da{" "}
        <code className="small">ACQ_INST_PAYIN</code> imza sətirində olub-olmaması
        bank sənədinə bağlıdır — imza rədd edilsə, yalnız installment builder
        tərəfdə genişləndirmə lazım ola bilər.
      </p>

      <ul className="nav nav-tabs mb-3">
        <li className="nav-item">
          <button
            type="button"
            className={`nav-link ${activeTab === TABS.plain ? "active" : ""}`}
            onClick={() => setActiveTab(TABS.plain)}
            disabled={busy}
          >
            Adi test
          </button>
        </li>
        <li className="nav-item">
          <button
            type="button"
            className={`nav-link ${activeTab === TABS.installment ? "active" : ""}`}
            onClick={() => setActiveTab(TABS.installment)}
            disabled={busy}
          >
            Taksit test
          </button>
        </li>
        <li className="nav-item">
          <button
            type="button"
            className={`nav-link ${activeTab === TABS.reversal ? "active" : ""}`}
            onClick={() => setActiveTab(TABS.reversal)}
            disabled={busy}
          >
            Reversal test
          </button>
        </li>
      </ul>

      {activeTab === TABS.plain ? (
        <form
          onSubmit={handlePlainSubmit}
          className="card card-body shadow-sm"
        >
          <p className="small text-muted mb-3">
            <code>POST …/api/TestPayment/build-form</code>
          </p>
          <div className="mb-3">
            <label className="form-label" htmlFor="tp-amount">
              amount (0 &lt; dəyər ≤ {CLIENT_MAX_AMOUNT})
            </label>
            <input
              id="tp-amount"
              type="number"
              step="0.01"
              min="0.01"
              max={CLIENT_MAX_AMOUNT}
              className="form-control"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              disabled={busy}
            />
          </div>
          <div className="mb-3">
            <label className="form-label" htmlFor="tp-name">
              name (istəyə bağlı — ASCII/Latin tövsiyə)
            </label>
            <input
              id="tp-name"
              type="text"
              className="form-control"
              autoComplete="off"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={busy}
            />
          </div>
          <div className="mb-3">
            <label className="form-label" htmlFor="tp-order">
              orderId (istəyə bağlı — rəqəm 6–32)
            </label>
            <input
              id="tp-order"
              type="text"
              inputMode="numeric"
              className="form-control"
              autoComplete="off"
              placeholder="məs. 123456"
              value={orderId}
              onChange={(e) => onOrderIdDigits(e.target.value, setOrderId)}
              disabled={busy}
            />
          </div>
          <button type="submit" className="btn btn-primary" disabled={busy}>
            {busy ? "Gözləyin…" : "POST — build-form"}
          </button>
        </form>
      ) : null}

      {activeTab === TABS.installment ? (
        <form
          onSubmit={handleInstallmentSubmit}
          className="card card-body shadow-sm"
        >
          <p className="small text-muted mb-3">
            <code>POST …/api/TestPayment/build-installment-form</code> — eyni
            JWT, HTML, test terminalı və MaxAmount qaydaları.
          </p>
          <div className="mb-3">
            <label className="form-label" htmlFor="ins-amount">
              amount (0 &lt; dəyər ≤ {CLIENT_MAX_AMOUNT})
            </label>
            <input
              id="ins-amount"
              type="number"
              step="0.01"
              min="0.01"
              max={CLIENT_MAX_AMOUNT}
              className="form-control"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              disabled={busy}
            />
          </div>
          <div className="mb-3">
            <label className="form-label" htmlFor="ins-months">
              installmentMonths (mütləq)
            </label>
            <select
              id="ins-months"
              className="form-select"
              value={installmentMonths}
              onChange={(e) => setInstallmentMonths(Number(e.target.value))}
              disabled={busy}
            >
              {INSTALLMENT_MONTHS.map((m) => (
                <option key={m} value={m}>
                  {m} ay
                </option>
              ))}
            </select>
          </div>
          <div className="mb-3">
            <label className="form-label" htmlFor="ins-name">
              name (istəyə bağlı)
            </label>
            <input
              id="ins-name"
              type="text"
              className="form-control"
              autoComplete="off"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={busy}
            />
          </div>
          <div className="mb-3">
            <label className="form-label" htmlFor="ins-order">
              orderId (istəyə bağlı — rəqəm 6–32)
            </label>
            <input
              id="ins-order"
              type="text"
              inputMode="numeric"
              className="form-control"
              autoComplete="off"
              value={orderId}
              onChange={(e) => onOrderIdDigits(e.target.value, setOrderId)}
              disabled={busy}
            />
          </div>
          <button type="submit" className="btn btn-primary" disabled={busy}>
            {busy ? "Gözləyin…" : "POST — build-installment-form"}
          </button>
        </form>
      ) : null}

      {activeTab === TABS.reversal ? (
        <form
          onSubmit={handleReversalSubmit}
          className="card card-body shadow-sm"
        >
          <p className="small text-muted mb-3">
            <code>POST …/api/TestPayment/build-reversal-form</code> — TRTYPE
            22; forma adətən{" "}
            <code className="small">https://mpi.3dsecure.az/cgi-bin/cgi_link</code>
            ünvanına post olunur; bu yol üçün{" "}
            <code className="small">TestPayment:GatewayUrl</code> istifadə
            olunmur.
          </p>
          <div className="mb-3">
            <label className="form-label" htmlFor="rev-amount">
              amount
            </label>
            <input
              id="rev-amount"
              type="number"
              step="0.01"
              min="0.01"
              max={CLIENT_MAX_AMOUNT}
              className="form-control"
              value={revAmount}
              onChange={(e) => setRevAmount(e.target.value)}
              disabled={busy}
            />
          </div>
          <div className="mb-3">
            <label className="form-label" htmlFor="rev-order">
              orderId (mütləq — rəqəm 6–32)
            </label>
            <input
              id="rev-order"
              type="text"
              inputMode="numeric"
              className="form-control"
              autoComplete="off"
              value={revOrderId}
              onChange={(e) => onOrderIdDigits(e.target.value, setRevOrderId)}
              disabled={busy}
            />
          </div>
          <div className="mb-3">
            <label className="form-label" htmlFor="rev-rrn">
              rrn (12 rəqəm)
            </label>
            <input
              id="rev-rrn"
              type="text"
              inputMode="numeric"
              className="form-control"
              autoComplete="off"
              value={rrn}
              onChange={(e) => onRrnDigits(e.target.value)}
              disabled={busy}
            />
          </div>
          <div className="mb-3">
            <label className="form-label" htmlFor="rev-intref">
              intRef (16–32 alphanumeric)
            </label>
            <input
              id="rev-intref"
              type="text"
              className="form-control"
              autoComplete="off"
              value={intRef}
              onChange={(e) => onIntRefAlnum(e.target.value)}
              disabled={busy}
            />
          </div>
          <button type="submit" className="btn btn-primary" disabled={busy}>
            {busy ? "Gözləyin…" : "POST — build-reversal-form"}
          </button>
        </form>
      ) : null}

      {info ? (
        <p className="alert alert-success mt-3 mb-0 small" role="status">
          {info}
        </p>
      ) : null}
      {error ? (
        <pre
          className="alert alert-danger mt-3 mb-0 small text-break"
          style={{ whiteSpace: "pre-wrap" }}
        >
          {error}
        </pre>
      ) : null}
      {checkoutHtml ? (
        <div className="mt-3 card card-body border-primary shadow-sm">
          <p className="small text-muted mb-3">
            Çərçivə (iframe) yoxdur — bank MPI cavabı üçün{" "}
            <code>X-Frame-Options</code> qəbul edilir. Brauzer pop-up icazəsi
            lazım ola bilər.
          </p>
          <button
            type="button"
            className="btn btn-primary"
            autoFocus
            onClick={openCheckoutHtmlInNewWindow}
          >
            Bank / MPI formunu yeni pəncərədə aç
          </button>
        </div>
      ) : null}
    </div>
  );
};

export default TestPaymentGatewayPage;
